import { create } from "zustand";

export interface DubUser {
  user_id: string;
  clerk_user_id: string;
  name: string;
  email: string;
  imageUrl: string;
}

export type ExclusionType = "username" | "email";

export interface ExclusionEntry {
  _id: string;
  value: string;
  added_at: string;
  added_by: string;
  type: ExclusionType;
}

interface OrganizationSettingsState {
  organizationInfo: any;
  dubUsers: DubUser[];
  excludedEntries: ExclusionEntry[];
  excludedEntriesCount: number;
  excludedEntriesLoading: boolean;
  isLoading: boolean;
  isGenerateFaqLoading?: boolean;
  error: string | null;
  fetchOrganizationInfo: (
    organizationId: string,
    userId: string,
    token: string,
  ) => Promise<any>;
  saveOrganizationSetting: (
    organizationId: string,
    userId: string,
    field: string,
    value: string | boolean | null | unknown[],
    token: string,
  ) => Promise<boolean>;
  generateFaqFromWebsite: (
    organizationId: string,
    userId: string,
    affiliatePageLink: string,
    websiteUrl: string,
    token: string,
  ) => Promise<{ success: boolean; faq_content?: string; error?: string }>;
  fetchDubUsers: (organizationId: string, token: string) => Promise<DubUser[]>;
  fetchExcludedEntries: (
    organizationId: string,
    token: string,
  ) => Promise<void>;
  addToExcludedEntries: (
    organizationId: string,
    userId: string,
    values: string[],
    type: ExclusionType,
    token: string,
  ) => Promise<{
    success: boolean;
    added?: number;
    duplicates_skipped?: number;
    error?: string;
  }>;
  removeFromExcludedEntries: (
    organizationId: string,
    values: string[],
    type: ExclusionType,
    token: string,
  ) => Promise<boolean>;
  clearExcludedEntries: (
    organizationId: string,
    token: string,
  ) => Promise<boolean>;
  reset: () => void;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export const useOrganizationSettingsStore = create<OrganizationSettingsState>(
  (set) => ({
    organizationInfo: null,
    dubUsers: [],
    excludedEntries: [],
    excludedEntriesCount: 0,
    excludedEntriesLoading: false,
    isLoading: false,
    error: null,

    fetchOrganizationInfo: async (
      organizationId: string,
      userId: string,
      token: string,
    ) => {
      set({ isLoading: true, error: null });
      try {
        const response = await fetch(`${API_BASE_URL}/api/settings/get`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            organization_id: organizationId,
            user_id: userId,
          }),
        });

        if (!response.ok)
          throw new Error("Failed to fetch organization settings");

        const data = await response.json();
        set({
          organizationInfo: {
            faq: data.data.organization_faq,
            custom_activation_message:
              data.data.organization_custom_activation_message,
            extracted_information: data.data.organization_extracted_information,
            website_url: data.data.organization_website_url,
            affiliate_page_link: data.data.organization_affiliate_page_link,
            dub_user_id: data.data.organization_dub_user_id,
            instagram_handle: data.data.organization_instagram_handle,
            tiktok_handle: data.data.organization_tiktok_handle,
            youtube_handle: data.data.organization_youtube_handle,
            custom_field_library: data.data.organization_custom_field_library ?? [],
          },
          isLoading: false,
        });

        return data;
      } catch (error) {
        set({ error: (error as Error).message, isLoading: false });
        return null;
      }
    },

    saveOrganizationSetting: async (
      organizationId: string,
      userId: string,
      field: string,
      value: string | boolean | null | unknown[],
      token: string,
    ) => {
      set({ isLoading: true, error: null });
      try {
        const response = await fetch(`${API_BASE_URL}/api/settings/save`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            organization_id: organizationId,
            user_id: userId,
            field,
            value,
          }),
        });

        if (!response.ok)
          throw new Error("Failed to save organization setting");

        const data = await response.json();

        if (field === "organization_faq") {
          set((state) => ({
            organizationInfo: { ...state.organizationInfo, faq: value },
            isLoading: false,
          }));
        } else if (field === "organization_custom_activation_message") {
          set((state) => ({
            organizationInfo: {
              ...state.organizationInfo,
              custom_activation_message: value,
            },
            isLoading: false,
          }));
        } else if (field === "organization_extracted_information") {
          set((state) => ({
            organizationInfo: {
              ...state.organizationInfo,
              extracted_information: value,
            },
            isLoading: false,
          }));
        } else if (field === "organization_website_url") {
          set((state) => ({
            organizationInfo: {
              ...state.organizationInfo,
              website_url: value,
            },
            isLoading: false,
          }));
        } else if (field === "organization_affiliate_page_link") {
          set((state) => ({
            organizationInfo: {
              ...state.organizationInfo,
              affiliate_page_link: value,
            },
            isLoading: false,
          }));
        } else if (field === "organization_dub_user_id") {
          set((state) => ({
            organizationInfo: {
              ...state.organizationInfo,
              dub_user_id: value,
            },
            isLoading: false,
          }));
        } else if (field === "organization_instagram_handle") {
          set((state) => ({
            organizationInfo: {
              ...state.organizationInfo,
              instagram_handle: value,
            },
            isLoading: false,
          }));
        } else if (field === "organization_tiktok_handle") {
          set((state) => ({
            organizationInfo: {
              ...state.organizationInfo,
              tiktok_handle: value,
            },
            isLoading: false,
          }));
        } else if (field === "organization_youtube_handle") {
          set((state) => ({
            organizationInfo: {
              ...state.organizationInfo,
              youtube_handle: value,
            },
            isLoading: false,
          }));
        }
        set({ isLoading: false });

        return data.success;
      } catch (error) {
        set({ error: (error as Error).message, isLoading: false });
        return false;
      }
    },

    generateFaqFromWebsite: async (
      organizationId: string,
      userId: string,
      affiliatePageLink: string,
      websiteUrl: string,
      token: string,
    ) => {
      set({ isGenerateFaqLoading: true, error: null });
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/settings/generate-faq`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              organization_id: organizationId,
              user_id: userId,
              website_url: websiteUrl,
              affiliate_page_link: affiliatePageLink,
            }),
          },
        );

        if (!response.ok) {
          const data = await response.json();
          if (data.error) {
            throw new Error(data.error);
          } else {
            throw new Error("Failed to generate FAQ");
          }
        }

        const data = await response.json();

        if (data.success && data.data?.faq_content) {
          set((state) => ({
            organizationInfo: {
              ...state.organizationInfo,
              faq: data.data.faq_content,
            },
            isGenerateFaqLoading: false,
          }));

          return { success: true, faq_content: data.data.faq_content };
        } else {
          throw new Error(data.error || "Failed to generate FAQ");
        }
      } catch (error) {
        const errorMessage = (error as Error).message;
        set({ error: errorMessage, isLoading: false });
        return { success: false, error: errorMessage };
      } finally {
        set({ isGenerateFaqLoading: false });
      }
    },

    fetchDubUsers: async (organizationId: string, token: string) => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/settings/get-dub-users`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              organization_id: organizationId,
            }),
          },
        );

        if (!response.ok) throw new Error("Failed to fetch Dub users");

        const data = await response.json();
        const dubUsers = data.data || [];
        set({ dubUsers });
        return dubUsers;
      } catch (error) {
        console.error("Error fetching Dub users:", error);
        set({ dubUsers: [] });
        return [];
      }
    },

    fetchExcludedEntries: async (organizationId: string, token: string) => {
      set({ excludedEntriesLoading: true });
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/settings/exclusion-list/get`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ organization_id: organizationId }),
          },
        );

        if (!response.ok) throw new Error("Failed to fetch excluded entries");

        const data = await response.json();
        set({
          excludedEntries: data.data?.excluded_creators || [],
          excludedEntriesCount: data.data?.count || 0,
          excludedEntriesLoading: false,
        });
      } catch (error) {
        console.error("Error fetching excluded entries:", error);
        set({ excludedEntriesLoading: false });
      }
    },

    addToExcludedEntries: async (
      organizationId: string,
      userId: string,
      values: string[],
      type: ExclusionType,
      token: string,
    ) => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/settings/exclusion-list/add`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              organization_id: organizationId,
              user_id: userId,
              values,
              type,
            }),
          },
        );

        const data = await response.json();
        if (!response.ok) throw new Error(data?.error || "Failed to add excluded entries");

        return {
          success: data.success,
          added: data.data?.added,
          duplicates_skipped: data.data?.duplicates_skipped,
          error: data.error,
        };
      } catch (error) {
        console.error("Error adding excluded entries:", error);
        return { success: false, error: (error as Error).message };
      }
    },

    removeFromExcludedEntries: async (
      organizationId: string,
      values: string[],
      type: ExclusionType,
      token: string,
    ) => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/settings/exclusion-list/remove`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              organization_id: organizationId,
              values,
              type,
            }),
          },
        );

        if (!response.ok) throw new Error("Failed to remove excluded entries");

        const data = await response.json();
        return data.success;
      } catch (error) {
        console.error("Error removing excluded entries:", error);
        return false;
      }
    },

    clearExcludedEntries: async (organizationId: string, token: string) => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/settings/exclusion-list/clear`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ organization_id: organizationId }),
          },
        );

        if (!response.ok) throw new Error("Failed to clear excluded entries");

        const data = await response.json();
        if (data.success) {
          set({ excludedEntries: [], excludedEntriesCount: 0 });
        }
        return data.success;
      } catch (error) {
        console.error("Error clearing excluded entries:", error);
        return false;
      }
    },

    reset: () =>
      set({
        organizationInfo: null,
        dubUsers: [],
        excludedEntries: [],
        excludedEntriesCount: 0,
        excludedEntriesLoading: false,
        isLoading: false,
        error: null,
      }),
  }),
);
